import { Form } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/app/hook';
import ProBadge from '@/components/ui/ProBadge';
import usePermission from '@/hooks/usePermission';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { handleResetAppearance, handleUpdateAppearanceItem } from './appearanceSlice';
import ColorDropDown from './ColorDropDown';
import { IAiAppearance } from './types';

function AppearanceTheme() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  const {
    register,
    formState: { errors },
    control,
  } = useFormContext<IAiAppearance>();

  return (
    <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
      <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
        {t('DashboardSettingCustomizeChat/l-06')}
      </div>
      <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
        <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-w-full">
          <div className="tw-w-full tw-col-span-12">
            <Form.Group>
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('DashboardSettingCustomizeChat/l-12')}
                {!permissions.settingsPage.widget.customization.theme && (
                  <ProBadge
                    className="tw-ms-2"
                    onClose={() => {
                      dispatch(handleResetAppearance());
                    }}
                  />
                )}
              </Form.Label>
              <Form.Select
                translate="no"
                disabled={!permissions.settingsPage.widget.customization.theme}
                isInvalid={!!errors.theme}
                {...register('theme', {
                  onChange(event) {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'theme',
                        value: event.target.value,
                      })
                    );
                  },
                })}
              >
                <option value={'light'}>{t('DashboardSettingCustomizeChat/l-05')}</option>
                <option value={'dark'}>{t('DashboardSettingCustomizeChat/l-07')}</option>
              </Form.Select>
              {/* Validation */}
              {errors.theme && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.theme?.message}</span>}
            </Form.Group>
          </div>
          <div className="tw-w-full tw-col-span-12">
            <Form.Group>
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('DashboardSettingCustomizeChat/t-01')}
                {!permissions.settingsPage.widget.customization.colors && (
                  <ProBadge
                    className="tw-ms-2"
                    onClose={() => {
                      dispatch(handleResetAppearance());
                    }}
                  />
                )}
              </Form.Label>
              <Controller
                control={control}
                name="color"
                render={({ field: { onChange, value } }) => (
                  <ColorDropDown
                    acceptableColors={[
                      { label: 'Grey', value: '#6B7280' },
                      { label: 'Red', value: '#EF4444' },
                      { label: 'Orange', value: '#F97316' },
                      { label: 'Yellow', value: '#F59E0B' },
                      { label: 'Green', value: '#10B981' },
                      { label: 'Teal', value: '#14B8A6' },
                      { label: 'Cyan', value: '#06B6D4' },
                      { label: 'Light Blue', value: '#0EA5E9' },
                      { label: 'Blue', value: '#3B82F6' },
                      { label: 'Indigo', value: '#6366F1' },
                      { label: 'Purple (Violet)', value: '#8B5CF6' },
                      { label: 'Pink', value: '#EC4899' },
                    ]}
                    disabled={!permissions.settingsPage.widget.customization.colors}
                    isInvalid={!!errors.color}
                    errorMessage={errors.color?.message}
                    onColorChanged={color => {
                      onChange(color);

                      dispatch(
                        handleUpdateAppearanceItem({
                          key: 'color',
                          value: color,
                        })
                      );
                    }}
                    currentColor={value}
                  />
                )}
              />
            </Form.Group>
          </div>
          <div className="tw-w-full tw-col-span-12">
            <Form.Group>
              <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
                {t('DashboardSettingCustomizeChat/t-02')}
                {!permissions.settingsPage.widget.customization.position && <ProBadge className="tw-ms-2" />}
              </Form.Label>
              <Form.Select
                translate="no"
                disabled={!permissions.settingsPage.widget.customization.position}
                isInvalid={!!errors.position}
                {...register('position', {
                  onChange(event) {
                    dispatch(
                      handleUpdateAppearanceItem({
                        key: 'position',
                        value: event.target.value,
                      })
                    );
                  },
                })}
              >
                <option value={'right'}>{t('DashboardSettingCustomizeChat/l-09')}</option>
                <option value={'left'}>{t('DashboardSettingCustomizeChat/l-08')}</option>
              </Form.Select>
              {/* Validation */}
              {errors.position && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.position?.message}</span>}
            </Form.Group>
          </div>
        </div>
      </div>
    </div>
  );
}
export default WrappedErrorBoundary(AppearanceTheme);
